import React, { useEffect, useState, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux";
import { getCurrentUser } from "../store/auth";
import { useNavigate } from "react-router-dom";
import { getAppTasks, getCandidateApps } from "../store/candidate";
import { getWorkflowTask, workflowTaskReceived } from "../store/WorkflowEngine";
import { useLoadCandidateAppsQuery, useLoadAppRuningTasksQuery } from "../store/candidateApiSlice";
import { useLazyLoadWorkflowTaskQuery } from "../store/workflowApiSlice";
import { setCurrentPageTitle, setCurrentRoute } from "../store/layoutUI";

const COMPLIANCE_STYLES = {
    shell: {
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        marginTop: "16px",
        borderRadius: "0",
        border: "1px solid #e5e5e5",
        backgroundColor: "#ffffff",
        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.04)",
        width: "100%"
    },
    hero: {
        padding: "18px 22px",
        borderBottom: "1px solid #e5e5e5",
        backgroundColor: "#ffffff"
    },
    heroRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "18px",
        flexWrap: "wrap"
    },
    heroCopy: {
        minWidth: "260px",
        flex: "1 1 480px"
    },
    heroTitleRow: {
        display: "flex",
        alignItems: "center",
        gap: "14px"
    },
    heroIcon: {
        width: "34px",
        height: "34px",
        borderRadius: "0",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#ffffff",
        color: "#000000",
        fontSize: "14px",
        border: "1px solid #e5e5e5",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
        flexShrink: 0
    },
    heroTitle: {
        fontSize: "18px",
        fontWeight: 600,
        lineHeight: 1.25,
        color: "#000000",
        margin: "0 0 3px 0",
        letterSpacing: "-0.01em"
    },
    heroText: {
        fontSize: "13px",
        lineHeight: 1.5,
        color: "#666666",
        margin: 0,
        maxWidth: "760px"
    },
    refreshButton: {
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        borderRadius: "0",
        border: "1px solid #e5e5e5",
        background: "#ffffff",
        color: "#333333",
        padding: "9px 18px",
        fontSize: "13px",
        fontWeight: 600,
        boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
        cursor: "pointer",
        transition: "all 0.2s ease"
    },
    statGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: "14px",
        padding: "20px 22px"
    },
    statCard: {
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        backgroundColor: "#ffffff",
        border: "1px solid #e5e5e5",
        borderRadius: "0",
        padding: "16px",
        boxShadow: "0 6px 18px rgba(0, 0, 0, 0.03)",
        transition: "all 0.2s ease"
    },
    statIcon: {
        width: "36px",
        height: "36px",
        borderRadius: "0",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#f5f5f5",
        color: "#000000",
        fontSize: "14px",
        flexShrink: 0
    },
    statContent: {
        minWidth: 0
    },
    statLabel: {
        fontSize: "11px",
        fontWeight: 600,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "#666666",
        marginBottom: "4px"
    },
    statValue: {
        fontSize: "24px",
        fontWeight: 600,
        lineHeight: 1,
        color: "#000000",
        marginBottom: "4px"
    },
    panel: {
        backgroundColor: "#ffffff",
        border: "1px solid #e5e5e5",
        borderRadius: "0",
        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.04)"
    },
    cardHeader: {
        padding: "18px 22px",
        borderBottom: "1px solid #e5e5e5",
        backgroundColor: "#ffffff"
    },
    headerRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "16px",
        flexWrap: "wrap"
    },
    titleRow: {
        display: "flex",
        alignItems: "center",
        gap: "12px"
    },
    titleIcon: {
        width: "34px",
        height: "34px",
        borderRadius: "0",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#ffffff",
        color: "#000000",
        fontSize: "14px",
        border: "1px solid #e5e5e5",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
        flexShrink: 0
    },
    panelTitle: {
        fontSize: "18px",
        fontWeight: 600,
        color: "#000000",
        margin: 0
    },
    panelText: {
        fontSize: "12px",
        color: "#666666",
        margin: 0
    },
    cardBody: {
        padding: "0"
    },
    tableWrap: {
        overflowX: "auto",
        backgroundColor: "#ffffff"
    },
    table: {
        width: "100%",
        marginBottom: 0,
        fontSize: "13px",
        color: "#333333"
    },
    tableHeadCell: {
        borderTop: "1px solid #e5e5e5",
        borderBottom: "1px solid #e5e5e5",
        fontSize: "11px",
        fontWeight: 600,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "#666666",
        background: "#f5f5f5",
        padding: "14px 16px",
        whiteSpace: "nowrap",
        verticalAlign: "top"
    },
    cell: {
        verticalAlign: "middle",
        padding: "14px 16px",
        borderBottom: "1px solid #e5e5e5",
        fontWeight: 600
    },
    serialBadge: {
        minWidth: "30px",
        height: "30px",
        borderRadius: "0",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#f5f5f5",
        color: "#000000",
        fontSize: "12px",
        fontWeight: 800
    },
    formNameText: {
        fontSize: "14px",
        fontWeight: 700,
        color: "#000000"
    },
    actionBtn: {
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        borderRadius: "0",
        border: "1px solid #e5e5e5",
        background: "#ffffff",
        color: "#333333",
        padding: "9px 18px",
        fontSize: "13px",
        fontWeight: 600,
        boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
        cursor: "pointer",
        transition: "all 0.2s ease",
        textDecoration: "none"
    },
    emptyState: {
        padding: "42px 24px",
        textAlign: "center",
        backgroundColor: "#ffffff",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center"
    },
    emptyIcon: {
        width: "64px",
        height: "64px",
        margin: "0 auto 14px auto",
        borderRadius: "0",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f5f5f5",
        color: "#000000",
        fontSize: "26px"
    },
    emptyTitle: {
        margin: 0,
        color: "#000000",
        fontSize: "18px",
        fontWeight: 600,
        textAlign: "center"
    },
    emptyText: {
        margin: "10px auto 0 auto",
        color: "#666666",
        fontSize: "14px",
        lineHeight: 1.6,
        fontWeight: 600,
        textAlign: "center"
    }
};

const COMPLIANCE_ROUTE = [
    { name: "Compliance Management", url: "" },
    { name: "Task List", url: "" },
];

export default function Compliance() {
    const [appTasklist, setAppTasklist] = useState([]);
    const [startBtnProccess, setStartBtnProccess] = useState(false);
    const [phaseName] = useState("Compliance");
    const [selectedTaskId, setSelectedTaskId] = useState(0);

    const workflowTaskSelector = useSelector(getWorkflowTask);

    const dispatch = useDispatch();
    const currentUser = getCurrentUser();
    const navigate = useNavigate();

    const usrId = currentUser?.id;

    // RTK Query hooks
    const { data: candidateAppsSelector = [], refetch: refetchCandidateApps } = useLoadCandidateAppsQuery(usrId, { skip: !usrId });

    const mainWorkflow = candidateAppsSelector.filter(x => x.workflowType === 1);
    const wf = mainWorkflow[0] || null;

    const { data: appTasksSelector = [], isLoading: isLoadingTasks, isFetching: isFetchingTasks } = useLoadAppRuningTasksQuery(
        { wfId: wf?.oldWorkflowId, userId: usrId },
        { skip: !wf?.oldWorkflowId || !usrId }
    );

    const [loadWorkflowTaskTrigger] = useLazyLoadWorkflowTaskQuery();

    useEffect(() => {
        dispatch(setCurrentPageTitle("Compliance Management"));
        dispatch(setCurrentRoute(COMPLIANCE_ROUTE));
    }, [dispatch]);

    useEffect(() => {
        if (appTasksSelector.length > 0) {
            const filterTasks = appTasksSelector.filter(x => x.taskType === 1 &&
                x.wiid > 0 &&
                x.wfPhase === phaseName);
            setAppTasklist(filterTasks);
        } else {
            setAppTasklist([]);
        }
    }, [appTasksSelector, phaseName]);

    const handleCurrentTask = useCallback((wiid) => {
        setStartBtnProccess(true);
        setSelectedTaskId(wiid);
        loadWorkflowTaskTrigger(wiid);
    }, [loadWorkflowTaskTrigger]);

    useEffect(() => {
        if (workflowTaskSelector && startBtnProccess) {
            setStartBtnProccess(false);
            if (workflowTaskSelector.formType === "CF")
                navigate("/" + workflowTaskSelector.formName, { state: { workflowTask: workflowTaskSelector } });
            else
                navigate("/DynamicForm", { state: { workflowTask: workflowTaskSelector } });

            dispatch(workflowTaskReceived(""));
        }
    }, [workflowTaskSelector, startBtnProccess, navigate, dispatch]);

    const handleRefresh = useCallback(() => {
        refetchCandidateApps();
    }, [refetchCandidateApps]);

    const usrType = localStorage.getItem("userType");

    const totalTasks = appTasklist.length;
    const pendingTasks = appTasklist.filter(x => !x.isCompleted).length;

    return <React.Fragment>
        <style>{`
            .compliance-stat-card:hover {
                transform: translateY(-2px);
                border-color: rgba(191, 219, 254, 0.9) !important;
                box-shadow: 0 10px 25px rgba(37, 99, 235, 0.07) !important;
            }
            .compliance-action-btn:hover {
                background-color: #f5f5f5 !important;
                transform: translateY(-1px);
                box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08) !important;
            }
            .compliance-refresh-btn:hover {
                background-color: #f5f5f5 !important;
                transform: translateY(-1px);
                box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08) !important;
            }
        `}</style>

        <div style={COMPLIANCE_STYLES.shell}>
            {/* Hero Card */}
            <div style={COMPLIANCE_STYLES.hero}>
                <div style={COMPLIANCE_STYLES.heroRow}>
                    <div style={COMPLIANCE_STYLES.heroCopy}>
                        <div style={COMPLIANCE_STYLES.heroTitleRow}>
                            <span style={COMPLIANCE_STYLES.heroIcon}>
                                <i className="fa fa-check-square-o" aria-hidden="true"></i>
                            </span>
                            <div>
                                <h1 style={COMPLIANCE_STYLES.heroTitle}>Compliance Management</h1>
                                <p style={COMPLIANCE_STYLES.heroText}>
                                    Review and acknowledge compliance-related forms and documents.
                                </p>
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="btn btn-sm compliance-refresh-btn"
                        style={COMPLIANCE_STYLES.refreshButton}
                        onClick={handleRefresh}
                    >
                        <i className="fa fa-refresh" aria-hidden="true"></i>
                        Refresh
                    </button>
                </div>
            </div>

            {/* Stat Cards */}
            <div style={COMPLIANCE_STYLES.statGrid}>
                    <div className="compliance-stat-card" style={COMPLIANCE_STYLES.statCard}>
                        <div style={COMPLIANCE_STYLES.statIcon}>
                            <i className="fa fa-tasks" aria-hidden="true"></i>
                        </div>
                        <div style={COMPLIANCE_STYLES.statContent}>
                            <div style={COMPLIANCE_STYLES.statLabel}>Total Tasks</div>
                            <div style={COMPLIANCE_STYLES.statValue}>{totalTasks}</div>
                        </div>
                    </div>
                    <div className="compliance-stat-card" style={COMPLIANCE_STYLES.statCard}>
                        <div style={{ ...COMPLIANCE_STYLES.statIcon, background: "#fffbeb", color: "#b45309" }}>
                            <i className="fa fa-clock-o" aria-hidden="true"></i>
                        </div>
                        <div style={COMPLIANCE_STYLES.statContent}>
                            <div style={COMPLIANCE_STYLES.statLabel}>Pending</div>
                            <div style={COMPLIANCE_STYLES.statValue}>{pendingTasks}</div>
                        </div>
                    </div>
                </div>

            <div style={COMPLIANCE_STYLES.cardBody}>
                    <div style={COMPLIANCE_STYLES.tableWrap}>
                        <div className="table-responsive">
                            <table className="table align-items-center table-hover mb-0 compliance-table" style={COMPLIANCE_STYLES.table}>
                                <thead>
                                    <tr>
                                        <th style={COMPLIANCE_STYLES.tableHeadCell}>#</th>
                                        <th style={COMPLIANCE_STYLES.tableHeadCell}>Form Name</th>
                                        <th style={COMPLIANCE_STYLES.tableHeadCell}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoadingTasks || (isFetchingTasks && appTasklist.length === 0) ? (
                                        <tr>
                                            <td colSpan="3" style={{ padding: "44px 20px", textAlign: "center", background: "#ffffff" }}>
                                                <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
                                                    <i className="fa fa-circle-o-notch fa-spin" style={{ fontSize: "26px", color: "#000000" }}></i>
                                                    <div style={{ fontSize: "14px", fontWeight: 600, color: "#000000" }}>Loading compliance tasks...</div>
                                                    <div style={{ fontSize: "12px", fontWeight: 600, color: "#666666" }}>Please wait while we retrieve task details.</div>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : appTasklist.length > 0 ? appTasklist.map((item, key) => (
                                        <tr key={item.wiid || key}>
                                            <td style={COMPLIANCE_STYLES.cell}>
                                                <span style={COMPLIANCE_STYLES.serialBadge}>{key + 1}</span>
                                            </td>
                                            <td style={COMPLIANCE_STYLES.cell}>
                                                <div style={COMPLIANCE_STYLES.formNameText}>{item.taskName}</div>
                                            </td>
                                            <td style={COMPLIANCE_STYLES.cell}>
                                                <button
                                                    onClick={() => handleCurrentTask(item.wiid)}
                                                    disabled={true}
                                                    title="Coming Soon"
                                                    className="btn btn-sm compliance-action-btn"
                                                    style={COMPLIANCE_STYLES.actionBtn}
                                                >
                                                    {startBtnProccess && selectedTaskId === item.wiid ? (
                                                        <i className="fa fa-spinner fa-spin" aria-hidden="true" style={{ color: "#000000" }}></i>
                                                    ) : (
                                                        <i className="fa fa-eye" aria-hidden="true" style={{ color: "#000000" }}></i>
                                                    )}
                                                    View / Acknowledgment
                                                </button>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="3" style={{ ...COMPLIANCE_STYLES.cell, padding: 0 }}>
                                                <div style={COMPLIANCE_STYLES.emptyState}>
                                                    <div style={COMPLIANCE_STYLES.emptyIcon}>
                                                        <i className="fa fa-check-square-o" aria-hidden="true"></i>
                                                    </div>
                                                    <h4 style={COMPLIANCE_STYLES.emptyTitle}>No compliance tasks found</h4>
                                                    <p style={COMPLIANCE_STYLES.emptyText}>
                                                        Compliance tasks will appear here once forms and documents are assigned to your account.
                                                    </p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
        </div>
    </React.Fragment>
}